import { createPortal } from 'react-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { formatContainerDisplayId } from '../lib/batch';
import { formatQty } from '../lib/quantity';
import { bottlingApi, type BottlingDetails, type BottlingListRow, type BottlingMeta } from '../services/bottling';

type CreateRow = {
  productId: string;
  productSearch: string;
  packagingBatchKey: string;
  packagingBatchCode: string;
  qtyUnits: string;
  unitWeightG: string;
};

type SourceRow = {
  containerId: string;
  qtyKg: string;
};

const EMPTY_CREATE_ROW: CreateRow = {
  productId: '',
  productSearch: '',
  packagingBatchKey: '',
  packagingBatchCode: '',
  qtyUnits: '',
  unitWeightG: '',
};

const EMPTY_SOURCE_ROW: SourceRow = {
  containerId: '',
  qtyKg: '',
};

function productOptionLabel(product: BottlingMeta['products'][number]): string {
  return product.name;
}

function packagingOptionLabel(option: BottlingMeta['packagingStock'][number]): string {
  const batchPart = option.batchCode ? option.batchCode : 'Utan batch';
  return `${option.productName} • ${batchPart} • ${formatQty(option.qtyOnHand, 'st')} kvar • ${option.siteName}`;
}

function packagingOptionKey(option: BottlingMeta['packagingStock'][number]): string {
  return `${option.sourceType}|${option.productId}|${option.siteId}|${option.batchCode ?? '__NULL__'}`;
}

function getMatchingProducts(products: BottlingMeta['products'], search: string) {
  const normalizedSearch = search.trim().toLowerCase();
  return products
    .filter((product) => {
      if (!normalizedSearch) return true;
      return product.name.toLowerCase().includes(normalizedSearch);
    })
    .slice(0, 12);
}

export function BottlingPage() {
  const toast = useToast();
  const { canWrite } = useAuth();
  const writable = canWrite('bottling');
  const currentYear = new Date().getFullYear();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialYear = Number(searchParams.get('year'));
  const initialSelectedId = Number(searchParams.get('id'));

  const [meta, setMeta] = useState<BottlingMeta | null>(null);
  const [rows, setRows] = useState<BottlingListRow[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(
    Number.isFinite(initialYear) && initialYear >= 2000 && initialYear <= 2100 ? initialYear : currentYear
  );
  const [selectedId, setSelectedId] = useState<number | null>(
    Number.isFinite(initialSelectedId) && initialSelectedId > 0 ? initialSelectedId : null
  );
  const [details, setDetails] = useState<BottlingDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingDetails, setLoadingDetails] = useState<boolean>(false);
  const [posting, setPosting] = useState<boolean>(false);

  const [createOpen, setCreateOpen] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [createDate, setCreateDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [sourceRows, setSourceRows] = useState<SourceRow[]>([{ ...EMPTY_SOURCE_ROW }]);
  const [targetSiteId, setTargetSiteId] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [creating, setCreating] = useState<boolean>(false);
  const [createRows, setCreateRows] = useState<CreateRow[]>([{ ...EMPTY_CREATE_ROW }]);
  const [openProductSearchIndex, setOpenProductSearchIndex] = useState<number | null>(null);
  const [productSearchDropdownStyle, setProductSearchDropdownStyle] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const productSearchInputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const selectedSourceContainers = useMemo(() => {
    return sourceRows.map((row) => {
      const containerId = Number(row.containerId);
      return Number.isFinite(containerId) && containerId > 0
        ? (meta?.containers ?? []).find((c) => c.id === containerId) ?? null
        : null;
    });
  }, [meta, sourceRows]);

  const packagingComponentsByProductId = useMemo(() => {
    const map = new Map<number, BottlingMeta['packagingComponents'][number]>();
    (meta?.packagingComponents ?? []).forEach((component) => {
      if (!map.has(component.productId)) {
        map.set(component.productId, component);
      }
    });
    return map;
  }, [meta]);

  const packagingOptionsByRow = useMemo(() => {
    return createRows.map((row) => {
      const productId = Number(row.productId);
      const component = Number.isFinite(productId) && productId > 0 ? packagingComponentsByProductId.get(productId) ?? null : null;
      if (!component) {
        return [] as BottlingMeta['packagingStock'];
      }
      return (meta?.packagingStock ?? [])
        .filter((option) => option.productId === component.packagingProductId)
        .sort((a, b) => {
          const batchA = a.batchCode ?? '';
          const batchB = b.batchCode ?? '';
          return batchA.localeCompare(batchB, 'sv') || a.siteName.localeCompare(b.siteName, 'sv');
        });
    });
  }, [createRows, meta, packagingComponentsByProductId]);

  const getPackagingOptionsForProductId = (productId: number) => {
    const component = packagingComponentsByProductId.get(productId) ?? null;
    if (!component) {
      return [] as BottlingMeta['packagingStock'];
    }
    return (meta?.packagingStock ?? [])
      .filter((option) => option.productId === component.packagingProductId)
      .sort((a, b) => {
        const batchA = a.batchCode ?? '';
        const batchB = b.batchCode ?? '';
        return batchA.localeCompare(batchB, 'sv') || a.siteName.localeCompare(b.siteName, 'sv');
      });
  };

  const load = async (year: number) => {
    setLoading(true);
    try {
      const [metaRes, listRes] = await Promise.all([
        bottlingApi.meta(),
        bottlingApi.list(year),
      ]);
      setMeta(metaRes);
      setRows(listRes.bottlings);
      setSelectedId(null);
      setDetails(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte ladda tappning');
    } finally {
      setLoading(false);
    }
  };

  const loadDetails = async (id: number) => {
    setLoadingDetails(true);
    try {
      setDetails(await bottlingApi.get(id));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte ladda tappningsdetalj');
      setDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  useEffect(() => {
    void load(selectedYear);
  }, [selectedYear]);

  useEffect(() => {
    const nextParams = new URLSearchParams();
    nextParams.set('year', String(selectedYear));
    if (selectedId) {
      nextParams.set('id', String(selectedId));
    }
    setSearchParams(nextParams, { replace: true });
  }, [selectedId, selectedYear, setSearchParams]);

  useEffect(() => {
    if (!selectedId) {
      setDetails(null);
      return;
    }
    void loadDetails(selectedId);
  }, [selectedId]);

  useEffect(() => {
    const firstContainer = selectedSourceContainers.find((container) => container !== null);
    if (firstContainer && !targetSiteId) {
      setTargetSiteId(String(firstContainer.siteId));
    }
  }, [selectedSourceContainers, targetSiteId]);

  useEffect(() => {
    if (openProductSearchIndex === null) {
      setProductSearchDropdownStyle(null);
      return;
    }

    const updateDropdownPosition = () => {
      const input = productSearchInputRefs.current[openProductSearchIndex] ?? null;
      if (!input) {
        setProductSearchDropdownStyle(null);
        return;
      }

      const rect = input.getBoundingClientRect();
      const viewportPadding = 8;
      const availableWidth = Math.max(220, window.innerWidth - viewportPadding * 2);
      const width = Math.min(Math.max(rect.width, 220), availableWidth);
      const left = Math.min(
        Math.max(viewportPadding, rect.left),
        Math.max(viewportPadding, window.innerWidth - width - viewportPadding)
      );
      const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
      const spaceAbove = rect.top - viewportPadding;
      const openUpwards = spaceBelow < 180 && spaceAbove > spaceBelow;
      const maxHeight = Math.max(120, Math.min(320, openUpwards ? spaceAbove - 8 : spaceBelow - 8));
      const top = openUpwards
        ? Math.max(viewportPadding, rect.top - maxHeight - 4)
        : Math.min(window.innerHeight - viewportPadding - maxHeight, rect.bottom + 4);

      setProductSearchDropdownStyle({ top, left, width, maxHeight });
    };

    // Capture scroll so position updates when modal/table ancestors move.
    updateDropdownPosition();
    window.addEventListener('resize', updateDropdownPosition);
    window.addEventListener('scroll', updateDropdownPosition, true);

    return () => {
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('scroll', updateDropdownPosition, true);
    };
  }, [openProductSearchIndex, createRows.length]);

  const resetCreate = () => {
    setCreateDate(new Date().toISOString().slice(0, 10));
    setSourceRows([{ ...EMPTY_SOURCE_ROW }]);
    setTargetSiteId('');
    setNotes('');
    setCreateRows([{ ...EMPTY_CREATE_ROW }]);
    setOpenProductSearchIndex(null);
  };

  const openCreateDialog = () => {
    setEditingId(null);
    resetCreate();
    setCreateOpen(true);
  };

  const openEditDialog = () => {
    if (!details) return;
    const packagingRowByLineId = new Map<number, BottlingDetails['packagingRows'][number]>();
    details.packagingRows.forEach((row) => {
      if (row.bottlingLineId !== null && !packagingRowByLineId.has(row.bottlingLineId)) {
        packagingRowByLineId.set(row.bottlingLineId, row);
      }
    });
    setEditingId(details.id);
    setCreateDate(String(details.bottlingDate).slice(0, 10));
    setSourceRows(
      details.sourceContainers.length > 0
        ? details.sourceContainers.map((row) => ({
            containerId: String(row.containerId),
            qtyKg: String(row.qtyKg),
          }))
        : [{ containerId: String(details.sourceContainerId), qtyKg: String(details.totalKg) }]
    );
    setTargetSiteId(String(details.targetSiteId));
    setNotes(details.notes ?? '');
    setCreateRows(
      details.lines.length > 0
        ? details.lines.map((line) => {
            const packagingRow = packagingRowByLineId.get(line.id) ?? null;
            const product = meta?.products.find((item) => item.id === line.productId) ?? null;
            const component = line.productId ? packagingComponentsByProductId.get(line.productId) ?? null : null;
            const stockOption = packagingRow && component
              ? (meta?.packagingStock ?? []).find((option) => option.productId === component.packagingProductId && option.batchCode === packagingRow.batchCode) ?? null
              : null;
            return {
              productId: String(line.productId),
              productSearch: product ? productOptionLabel(product) : line.productName,
              packagingBatchKey: stockOption ? packagingOptionKey(stockOption) : '',
              packagingBatchCode: packagingRow?.batchCode ?? '',
              qtyUnits: String(line.qtyUnits),
              unitWeightG: String(line.unitWeightG),
            };
          })
        : [{ ...EMPTY_CREATE_ROW }]
    );
    setOpenProductSearchIndex(null);
    setCreateOpen(true);
  };

  const totalKg = useMemo(() => {
    return createRows.reduce((sum, row) => {
      const units = Number(row.qtyUnits);
      const weight = Number(row.unitWeightG);
      if (Number.isNaN(units) || Number.isNaN(weight) || units <= 0 || weight <= 0) {
        return sum;
      }
      return sum + (units * weight) / 1000;
    }, 0);
  }, [createRows]);

  const sourceTotalKg = useMemo(() => {
    return sourceRows.reduce((sum, row) => {
      const qtyKg = Number(row.qtyKg);
      if (Number.isNaN(qtyKg) || qtyKg <= 0) {
        return sum;
      }
      return sum + qtyKg;
    }, 0);
  }, [sourceRows]);

  const statusLabel = (status: 'DRAFT' | 'POSTED') => (status === 'POSTED' ? 'Klar' : 'Utkast');

  const updateCreateRow = (index: number, patch: Partial<CreateRow>) => {
    setCreateRows((current) => current.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)));
  };

  const updateSourceRow = (index: number, patch: Partial<SourceRow>) => {
    setSourceRows((current) =>
      current.map((row, rowIndex) => {
        if (rowIndex !== index) {
          return row;
        }
        const nextRow = { ...row, ...patch };
        if (patch.containerId !== undefined) {
          const containerId = Number(patch.containerId);
          const container = Number.isFinite(containerId) && containerId > 0
            ? (meta?.containers ?? []).find((entry) => entry.id === containerId) ?? null
            : null;
          if (container) {
            nextRow.qtyKg = String(container.remainingKg);
          }
        }
        return nextRow;
      })
    );
  };

  const handleProductSelect = (index: number, productId: number) => {
    const product = meta?.products.find((item) => item.id === productId);
    if (!product) return;
    const packagingOptions = getPackagingOptionsForProductId(product.id);
    const defaultPackagingOption = packagingOptions.length === 1 ? packagingOptions[0] : null;
    updateCreateRow(index, {
      productId: String(product.id),
      productSearch: productOptionLabel(product),
      packagingBatchKey: defaultPackagingOption ? packagingOptionKey(defaultPackagingOption) : '',
      packagingBatchCode: defaultPackagingOption?.batchCode ?? '',
      unitWeightG: product.netWeightG ? String(product.netWeightG) : '',
    });
    setOpenProductSearchIndex(null);
  };

  const saveBottling = async () => {
    if (!targetSiteId) {
      toast.error('Välj målplats');
      return;
    }

    const sourceContainers = sourceRows
      .filter((row) => row.containerId.trim() !== '' && row.qtyKg.trim() !== '')
      .map((row) => ({
        containerId: Number(row.containerId),
        qtyKg: Number(row.qtyKg),
      }))
      .filter((row) => row.containerId > 0 && row.qtyKg > 0);

    if (sourceContainers.length === 0) {
      toast.error('Lägg till minst en källhink');
      return;
    }

    const lines = createRows
      .filter((row) => row.productId && row.qtyUnits.trim() !== '' && row.unitWeightG.trim() !== '')
      .map((row) => ({
        productId: Number(row.productId),
        qtyUnits: Number(row.qtyUnits),
        unitWeightG: Number(row.unitWeightG),
        packagingBatchKey: row.packagingBatchKey,
        packagingBatchCode: row.packagingBatchCode.trim() || null,
      }))
      .filter((row) => row.productId > 0 && row.qtyUnits > 0 && row.unitWeightG > 0);

    if (lines.length === 0) {
      toast.error('Lägg till minst en färdigvarurad');
      return;
    }

    for (let index = 0; index < createRows.length; index += 1) {
      const row = createRows[index];
      if (!row.productId) continue;
      const productId = Number(row.productId);
      const component = packagingComponentsByProductId.get(productId);
      if (component && row.packagingBatchKey.trim() === '') {
        toast.error(`Välj emballagebatch på rad ${index + 1}`);
        return;
      }
    }

    setCreating(true);
    try {
      const payload = {
        bottlingDate: createDate,
        sourceContainers,
        targetSiteId: Number(targetSiteId),
        lines,
        notes,
      };
      const result = editingId
        ? await bottlingApi.update(editingId, payload)
        : await bottlingApi.create(payload);
      toast.success(editingId ? 'Tappning sparad' : `Tappning skapad: ${result.bottlingCode}`);
      setCreateOpen(false);
      setEditingId(null);
      resetCreate();
      await load(selectedYear);
      setSelectedId(result.id);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : editingId ? 'Kunde inte spara tappning' : 'Kunde inte skapa tappning');
    } finally {
      setCreating(false);
    }
  };

  const postBottling = async () => {
    if (!details) return;
    setPosting(true);
    try {
      await bottlingApi.post(details.id);
      toast.success('Tappning slutförd');
      await load(selectedYear);
      await loadDetails(details.id);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Kunde inte slutföra tappning');
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-lg font-semibold">Tappning</h1>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <button type="button" className="btn btn-secondary px-2 py-1" onClick={() => setSelectedYear((v) => v - 1)}>
              PREV
            </button>
            <span className="min-w-16 text-center text-sm font-semibold">{selectedYear}</span>
            {selectedYear < currentYear && (
              <button type="button" className="btn btn-secondary px-2 py-1" onClick={() => setSelectedYear((v) => Math.min(currentYear, v + 1))}>
                NEXT
              </button>
            )}
          </div>
          {writable && (
            <button type="button" className="btn btn-primary px-2 py-1" onClick={openCreateDialog}>
              Ny tappning
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Laddar...</p>
      ) : (
        <div className="rounded border border-gray-200 overflow-hidden">
          <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 14rem)' }}>
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-100 border-b border-gray-200">
                <tr>
                  <th className="text-left px-2 py-1.5">Kod</th>
                  <th className="text-left px-2 py-1.5">Datum</th>
                  <th className="text-left px-2 py-1.5">Källa</th>
                  <th className="text-left px-2 py-1.5">Produkt</th>
                  <th className="text-left px-2 py-1.5">Antal</th>
                  <th className="text-left px-2 py-1.5">Kg</th>
                  <th className="text-left px-2 py-1.5">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className={`border-t border-gray-100 cursor-pointer hover:bg-orange-50 ${selectedId === r.id ? 'bg-orange-50' : ''}`}
                    onClick={() => setSelectedId(r.id)}
                  >
                    <td className="px-2 py-1.5">{r.bottlingCode}</td>
                    <td className="px-2 py-1.5">{String(r.bottlingDate).slice(0, 10)}</td>
                    <td className="px-2 py-1.5">{formatContainerDisplayId(r.sourceContainerId, r.sourceContainerCode)}</td>
                    <td className="px-2 py-1.5">
                      {r.productLines.length > 0 ? (
                        <div className="space-y-0.5">
                          {r.productLines.map((line, index) => (
                            <div key={`${line.productId}-${index}`} className="leading-5">
                              <span>{line.productName}</span>
                              <span className="ml-2 text-xs text-gray-600">{line.qtyUnits} st</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div>{r.productName ?? '-'}</div>
                      )}
                    </td>
                    <td className="px-2 py-1.5">{r.qtyUnits}</td>
                    <td className="px-2 py-1.5">{formatQty(r.totalKg, 'kg')}</td>
                    <td className="px-2 py-1.5">{statusLabel(r.status)}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-2 py-4 text-sm text-gray-500">Inga tappningar för {selectedYear}.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {createOpen && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/30 p-3 sm:p-4">
          <div className="max-h-[94vh] w-full max-w-6xl overflow-y-auto rounded border border-gray-200 bg-white p-3 space-y-2">
            <h2 className="text-base font-semibold">{editingId ? 'Redigera tappning' : 'Ny tappning'}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <label className="text-sm block">
                Datum
                <input type="text" inputMode="numeric" placeholder="ÅÅÅÅ-MM-DD" className="mt-1 w-full" value={createDate} onChange={(e) => setCreateDate(e.target.value)} />
              </label>
              <label className="text-sm block">
                Målplats
                <select className="mt-1 w-full" value={targetSiteId} onChange={(e) => setTargetSiteId(e.target.value)}>
                  <option value="">Välj plats</option>
                  {(meta?.sites ?? []).map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>
              </label>
              <div className="text-sm block md:col-span-2 space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium">Källhinkar</div>
                  <button
                    type="button"
                    className="btn btn-secondary px-2 py-1"
                    onClick={() => setSourceRows((current) => [...current, { ...EMPTY_SOURCE_ROW }])}
                  >
                    Lägg till hink
                  </button>
                </div>
                <div className="overflow-x-auto rounded border border-gray-200">
                  <table className="min-w-full text-sm">
                    <thead className="sticky top-0 bg-gray-50">
                      <tr>
                        <th className="px-2 py-1.5 text-left font-medium">Hink-ID</th>
                        <th className="px-2 py-1.5 text-left font-medium">Kg</th>
                        <th className="px-2 py-1.5 text-left font-medium">Info</th>
                        <th className="px-2 py-1.5 text-right font-medium">Åtgärd</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sourceRows.map((row, index) => {
                        const container = selectedSourceContainers[index];
                        return (
                          <tr key={index} className="border-t border-gray-200 align-top">
                            <td className="px-2 py-1.5">
                              <input
                                type="number"
                                min="1"
                                className="w-full min-w-24"
                                value={row.containerId}
                                onChange={(e) => updateSourceRow(index, { containerId: e.target.value })}
                                placeholder="Hink-ID"
                              />
                            </td>
                            <td className="px-2 py-1.5">
                              <input
                                type="number"
                                min="0.001"
                                step="0.001"
                                className="w-full min-w-24"
                                value={row.qtyKg}
                                onChange={(e) => updateSourceRow(index, { qtyKg: e.target.value })}
                                placeholder="0,000"
                              />
                            </td>
                            <td className="px-2 py-1.5 text-xs text-gray-600">
                              {container
                                ? `${formatContainerDisplayId(container.id, container.containerCode)} • ${container.productName} • kvar ${formatQty(container.remainingKg, 'kg')} kg • ${container.siteName}`
                                : ''}
                            </td>
                            <td className="px-2 py-1.5 text-right">
                              <button
                                type="button"
                                className="rounded border border-red-200 px-2 py-1 text-sm text-red-700 hover:bg-red-50"
                                onClick={() => setSourceRows((current) => (current.length > 1 ? current.filter((_, rowIndex) => rowIndex !== index) : current))}
                              >
                                Ta bort
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              <label className="text-sm block md:col-span-2">
                Källsumma
                <div className="mt-1 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                  {sourceTotalKg > 0 ? `${formatQty(sourceTotalKg, 'kg')} kg från källhinkar` : 'Ingen källmängd angiven än'}
                </div>
              </label>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-medium">Färdigvarurader</div>
                <button
                  type="button"
                  className="btn btn-success px-2 py-1"
                  onClick={() => setCreateRows((current) => [...current, { ...EMPTY_CREATE_ROW }])}
                >
                  Lägg till rad
                </button>
              </div>
              <div className="overflow-x-auto rounded border border-gray-200">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-medium">Produkt</th>
                      <th className="px-2 py-1.5 text-left font-medium">Emballagebatch</th>
                      <th className="px-2 py-1.5 text-left font-medium">Antal</th>
                      <th className="px-2 py-1.5 text-left font-medium">Vikt/enhet</th>
                      <th className="px-2 py-1.5 text-right font-medium">Åtgärd</th>
                    </tr>
                  </thead>
                  <tbody>
                    {createRows.map((row, index) => {
                      const packagingOptions = packagingOptionsByRow[index] ?? [];

                      return (
                        <tr key={index} className="border-t border-gray-200 align-top">
                          <td className="px-2 py-1.5">
                            <div>
                              <input
                                className="w-full min-w-56"
                                ref={(element) => {
                                  productSearchInputRefs.current[index] = element;
                                }}
                                value={row.productSearch}
                                placeholder="Sök produkt"
                                onFocus={() => setOpenProductSearchIndex(index)}
                                onChange={(e) => updateCreateRow(index, {
                                  productId: '',
                                  productSearch: e.target.value,
                                  packagingBatchKey: '',
                                  packagingBatchCode: '',
                                })}
                                onBlur={() => {
                                  window.setTimeout(() => {
                                    setOpenProductSearchIndex((current) => (current === index ? null : current));
                                    setCreateRows((current) =>
                                      current.map((entry, rowIndex) => {
                                        if (rowIndex !== index || entry.productId === '') return entry;
                                        const product = (meta?.products ?? []).find((candidate) => candidate.id === Number(entry.productId));
                                        return product ? { ...entry, productSearch: productOptionLabel(product) } : entry;
                                      })
                                    );
                                  }, 120);
                                }}
                              />
                            </div>
                          </td>
                          <td className="px-2 py-1.5">
                            <select
                              className="w-full min-w-64"
                              value={row.packagingBatchKey}
                              onChange={(e) => {
                                const selectedOption = packagingOptions.find((option) => packagingOptionKey(option) === e.target.value) ?? null;
                                updateCreateRow(index, {
                                  packagingBatchKey: e.target.value,
                                  packagingBatchCode: selectedOption?.batchCode ?? '',
                                });
                              }}
                              disabled={!row.productId || packagingOptions.length === 0}
                            >
                              <option value="">
                                {row.productId ? (packagingOptions.length === 0 ? 'Ingen batch' : 'Välj emballagebatch') : 'Välj produkt först'}
                              </option>
                              {packagingOptions.map((option) => {
                                const value = packagingOptionKey(option);
                                return (
                                  <option key={value} value={value}>
                                    {packagingOptionLabel(option)}
                                  </option>
                                );
                              })}
                            </select>
                          </td>
                          <td className="px-2 py-1.5">
                            <input
                              type="number"
                              min={1}
                              className="w-full min-w-24"
                              value={row.qtyUnits}
                              onChange={(e) => updateCreateRow(index, { qtyUnits: e.target.value })}
                            />
                          </td>
                          <td className="px-2 py-1.5">
                            <input
                              type="number"
                              min={1}
                              className="w-full min-w-28"
                              value={row.unitWeightG}
                              onChange={(e) => updateCreateRow(index, { unitWeightG: e.target.value })}
                            />
                          </td>
                          <td className="px-2 py-1.5 text-right">
                            <button
                              type="button"
                              className="rounded border border-red-200 px-2 py-1 text-sm text-red-700 hover:bg-red-50"
                              onClick={() => setCreateRows((current) => (current.length > 1 ? current.filter((_, rowIndex) => rowIndex !== index) : current))}
                            >
                              Ta bort
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="text-sm text-gray-600">
              Total mängd: <span className="font-semibold">{formatQty(totalKg, 'kg')} kg</span>
              {sourceTotalKg > 0 && <span className="ml-2">• Källhinkar: {formatQty(sourceTotalKg, 'kg')} kg</span>}
            </div>
            <label className="text-sm block">
              Kommentar
              <textarea className="mt-1 w-full min-h-[70px]" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </label>
            <div className="sticky bottom-0 -mx-3 flex justify-end gap-2 border-t border-gray-200 bg-white px-3 pt-2">
              <button
                type="button"
                className="btn btn-secondary px-2 py-1"
                onClick={() => {
                  setCreateOpen(false);
                  setEditingId(null);
                }}
              >
                Avbryt
              </button>
              <button type="button" className="btn btn-success px-2 py-1" onClick={saveBottling} disabled={creating}>
                {creating ? 'Sparar...' : editingId ? 'Spara ändringar' : 'Skapa tappning'}
              </button>
            </div>
          </div>
        </div>
      )}

      {createOpen && openProductSearchIndex !== null && productSearchDropdownStyle && createPortal(
        <div
          className="fixed z-[90] overflow-auto rounded border border-gray-200 bg-white shadow-lg"
          style={{
            top: productSearchDropdownStyle.top,
            left: productSearchDropdownStyle.left,
            width: productSearchDropdownStyle.width,
            maxHeight: productSearchDropdownStyle.maxHeight,
          }}
        >
          {getMatchingProducts(
            meta?.products ?? [],
            createRows[openProductSearchIndex]?.productSearch ?? ''
          ).map((product) => (
            <button
              key={product.id}
              type="button"
              onMouseDown={(event) => {
                event.preventDefault();
                handleProductSelect(openProductSearchIndex, product.id);
              }}
              className="block w-full px-3 py-2 text-left text-sm hover:bg-orange-50"
            >
              {productOptionLabel(product)}
            </button>
          ))}
          {getMatchingProducts(
            meta?.products ?? [],
            createRows[openProductSearchIndex]?.productSearch ?? ''
          ).length === 0 && <div className="px-3 py-2 text-sm text-gray-500">Ingen träff</div>}
        </div>,
        document.body
      )}

      {selectedId && !createOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-2xl rounded border border-gray-200 bg-white p-3 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-base font-semibold">
                {details?.bottlingCode ?? (loadingDetails ? 'Laddar detalj...' : `Tappning #${selectedId}`)}
              </h2>
              <button type="button" className="btn btn-secondary px-2 py-1" onClick={() => setSelectedId(null)}>
                Stäng
              </button>
            </div>

            {loadingDetails && <p className="text-sm text-gray-500">Laddar detalj...</p>}

            {details && !loadingDetails && (
              <>
                <div className="flex items-start justify-between gap-3">
                  <div className="text-sm text-gray-600">
                    <div>Datum: {String(details.bottlingDate).slice(0, 10)}</div>
                    <div>Källcontainer: {formatContainerDisplayId(details.sourceContainerId, details.sourceContainerCode)}</div>
                    <div>
                      Källdata:
                      {details.sourceWaterPercentSnapshot !== null ? ` ${details.sourceWaterPercentSnapshot}%` : ' -'}
                      {details.sourceNectarLabel ? ` • ${details.sourceNectarLabel}` : ''}
                    </div>
                    <div>Källplats: {details.sourceSiteName}</div>
                    <div>Målplats: {details.targetSiteName}</div>
                    <div>Totalt: {details.totalUnits} st • {formatQty(details.totalKg, 'kg')} kg</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded ${details.status === 'POSTED' ? 'bg-success/15 text-success' : 'bg-orange-100 text-orange-800'}`}>
                      {statusLabel(details.status)}
                    </span>
                    {writable && (
                      <button type="button" className="btn btn-secondary px-2 py-1" onClick={openEditDialog}>
                        Redigera
                      </button>
                    )}
                  </div>
                </div>

                {details.notes && <p className="text-sm text-gray-700">{details.notes}</p>}

                <div className="space-y-1">
                  <div className="text-sm font-medium">Färdigvarurader</div>
                  {details.lines.map((line) => {
                    const linkedPackagingRows = details.packagingRows.filter(
                      (row) => row.bottlingLineId !== null && String(row.bottlingLineId) === String(line.id)
                    );
                    return (
                      <div key={line.id} className="rounded border border-gray-200 px-2 py-1 text-sm">
                        <div className="font-medium">{line.productName}</div>
                        <div className="text-xs text-gray-600">
                          {line.qtyUnits} st • {formatQty(line.unitWeightG, 'g')} g • {formatQty(line.totalKg, 'kg')} kg • Batch {line.batchCode}
                        </div>
                        <div className="mt-1 space-y-1">
                          {linkedPackagingRows.map((row) => (
                            <div key={row.id} className="rounded bg-gray-50 px-2 py-1 text-xs text-gray-700">
                              Emballage: {row.batchCode ? `${row.batchCode} • ` : ''}
                              {row.productName} • {row.qtyUnits} st
                              {row.legacyMaterialId ? ` • Legacy matid ${row.legacyMaterialId}` : ''}
                            </div>
                          ))}
                          {linkedPackagingRows.length === 0 && (
                            <div className="text-xs text-gray-500">Inget emballage kopplat till raden.</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="space-y-1">
                  <div className="text-sm font-medium">Emballagerader</div>
                  {details.packagingRows.filter((row) => row.bottlingLineId === null).map((row) => (
                    <div key={row.id} className="rounded border border-gray-200 px-2 py-1 text-sm">
                      <div className="font-medium">{row.batchCode ? `${row.batchCode} • ${row.productName}` : row.productName}</div>
                      <div className="text-xs text-gray-600">
                        {row.qtyUnits} st{row.legacyMaterialId ? ` • Legacy matid ${row.legacyMaterialId}` : ''}
                      </div>
                    </div>
                  ))}
                  {details.packagingRows.filter((row) => row.bottlingLineId === null).length === 0 && details.packagingRows.length > 0 && (
                    <div className="rounded border border-dashed border-gray-300 px-2 py-3 text-sm text-gray-500">
                      Alla emballagerader är kopplade till färdigvaruraderna ovan.
                    </div>
                  )}
                  {details.packagingRows.length === 0 && (
                    <div className="rounded border border-dashed border-gray-300 px-2 py-3 text-sm text-gray-500">
                      Inga emballagerader registrerade.
                    </div>
                  )}
                </div>

                {writable && details.status !== 'POSTED' && (
                  <div className="flex justify-end">
                    <button type="button" className="btn btn-success px-2 py-1" onClick={postBottling} disabled={posting}>
                      {posting ? 'Slutför...' : 'Slutför tappning'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
